import type { AchievementId, Medal } from '@tg/shared';
import { ART_PAL, mix, svgDoc, type ArtPal, type ArtTheme } from '../covers/kit';
import { frameMarkup } from './frame';
import { ICONS, MOMENT_ICONS } from './icons';

/*
 * Achievement badges: pure SVG strings (the server draws the same markup into share cards via a relative import),
 * so no React, DOM, Vite or `@/` aliases here. viewBox 128, no text, no images, no links; ids prefixed per badge,
 * medal, theme and optional suffix so any number of badges can live on one page.
 */
export const BADGE_SIZE = 128;

const prefix = (id: string, medal: Medal | null, theme: ArtTheme, suffix: string) =>
  `b${id.replace(/[^a-z0-9]/g, '')}${medal?.[0] ?? 'n'}${theme[0]}${suffix}`;

const ACCENTS = ['teal', 'orange', 'gold', 'red', 'blue', 'pink', 'purple', 'green', 'sky'] as const;

/**
 * Not earned: the same icon in a muted palette (accents pulled to a neutral grey). No CSS filters — resvg
 * (server cards) does not support them — and no "almost there" glow.
 */
function mutedPal(P: ArtPal): ArtPal {
  const m = { ...P };
  const grey = P.dark ? '#5A6579' : '#A3ACBB';
  for (const k of ACCENTS) m[k] = mix(P[k], grey, 0.78);
  m.ink = P.dark ? '#8C96A8' : '#6B7486';
  return m;
}

/** Inner markup (viewBox 0 0 128 128) for inlining into another SVG (cards, sprites). */
export function badgeMarkup(id: AchievementId, medal: Medal | null, theme: ArtTheme, suffix = ''): string {
  const P = ART_PAL[theme];
  const p = prefix(id, medal, theme, suffix);
  return frameMarkup(P, p, medal) + ICONS[id](medal ? P : mutedPal(P), `${p}i`);
}

export function badgeSvg(id: AchievementId, medal: Medal | null, theme: ArtTheme): string {
  return svgDoc(BADGE_SIZE, BADGE_SIZE, badgeMarkup(id, medal, theme));
}

/** Icons of moments on cards: 'win' (rising arrow with an orbit, no coins), 'place' (laurel with a ribbon). */
export function momentIconMarkup(kind: 'win' | 'place', theme: ArtTheme, suffix = ''): string {
  const P = ART_PAL[theme];
  const p = `mo${kind}${theme[0]}${suffix}`;
  return frameMarkup(P, p, 'gold') + MOMENT_ICONS[kind](P, `${p}i`);
}
